import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useSeo } from "@/hooks/useSeo";

export default function Designers() {
  const { data, loading } = useFetch("/store/brands");
  const [q, setQ] = useState("");
  useSeo("Designers A–Z", "Browse every designer stocked at Maison Éclat.");
  const grouped = useMemo(() => {
    const items = data?.items || [];
    const letters = {};
    for (const b of items) {
      if (q && !b.name.toLowerCase().includes(q.toLowerCase())) continue;
      const l = (b.name[0] || "#").toUpperCase();
      (letters[l] = letters[l] || []).push(b);
    }
    return Object.keys(letters).sort().map((l) => ({ letter: l, items: letters[l] }));
  }, [data, q]);
  const totalShown = grouped.reduce((a, g) => a + g.items.length, 0);
  return (
    <div className="container-lux pt-8 pb-24" data-testid="designers-page">
      <nav className="text-xs text-muted-foreground mb-6" data-testid="breadcrumbs"><Link to="/" className="hover:text-foreground">Home</Link><span className="mx-1.5">/</span><span className="text-foreground">Designers</span></nav>
      <header className="max-w-2xl mb-10"><h1 className="text-5xl sm:text-6xl font-medium">Designers A–Z</h1><p className="text-foreground/70 mt-4 leading-relaxed">Every house we carry — mostly independent ateliers and small houses, chosen one by one for their craft.</p></header>
      <div className="flex items-center gap-3 bg-white border rounded-md h-12 px-4 max-w-md mb-10" data-testid="designers-search"><Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search designers…" className="flex-1 outline-none bg-transparent text-sm" aria-label="Search designers" data-testid="designers-search-input" /></div>
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && totalShown === 0 && <p className="text-sm text-muted-foreground" data-testid="designers-empty">No designers match “{q}”.</p>}
      <div className="space-y-10" data-testid="designers-groups">
        {grouped.map((g) => (
          <section key={g.letter} data-testid={`designers-letter-${g.letter}`}>
            <div className="flex items-baseline gap-5 border-b pb-2 mb-5"><span className="font-display text-4xl">{g.letter}</span><span className="text-xs text-muted-foreground tabular">{g.items.length}</span></div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2.5">
              {g.items.map((b) => (
                <li key={b.slug}>
                  <Link to={`/designers/${b.slug}`} className="group flex items-baseline justify-between py-1 hover:text-foreground" data-testid={`designers-link-${b.slug}`}>
                    <span className="text-sm font-medium group-hover:underline underline-offset-4">{b.name}{b.country && <span className="text-muted-foreground ml-2">· {b.country}</span>}</span>
                    <span className="text-xs text-muted-foreground tabular">{b.product_count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
